<template>
  <div>
    <el-card>
      <el-form :inline="true" @submit.prevent>
        <el-form-item label="统计窗口">
          <el-select v-model="days" style="width: 140px" @change="loadAll">
            <el-option :value="7" label="最近7天" />
            <el-option :value="14" label="最近14天" />
            <el-option :value="30" label="最近30天" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="loading" @click="loadAll">刷新</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="12" style="margin-top: 12px;">
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover">
          <div class="stat-label">订阅页访问</div>
          <div class="stat-value">{{ summary.pageView }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover">
          <div class="stat-label">点击购买</div>
          <div class="stat-value">{{ summary.clickBuy }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="8">
        <el-card shadow="hover">
          <div class="stat-label">支付成功</div>
          <div class="stat-value">{{ summary.buySuccess }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" style="margin-top: 12px;">
      <el-col :xs="24" :sm="12">
        <el-card shadow="hover">
          <div class="stat-label">点击率（点击/访问）</div>
          <div class="stat-value">{{ summary.clickRate }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12">
        <el-card shadow="hover">
          <div class="stat-label">支付转化率（支付/点击）</div>
          <div class="stat-value">{{ summary.payRate }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" style="margin-top: 12px;">
      <el-col :xs="24" :lg="12">
        <el-card>
          <template #header>订阅漏斗</template>
          <el-table :data="funnel.steps || []" stripe border size="small" v-loading="loading">
            <el-table-column prop="event_name" label="事件" min-width="180" />
            <el-table-column prop="event_count" label="事件数" width="100" />
            <el-table-column prop="user_count" label="用户数" width="100" />
            <el-table-column label="用户转化率" width="120">
              <template #default="{ row }">{{ toPercent(row.user_convert) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card>
          <template #header>事件聚合</template>
          <el-table :data="metrics.list || []" stripe border size="small" v-loading="loading">
            <el-table-column prop="event_name" label="事件" min-width="160" />
            <el-table-column prop="source" label="来源" min-width="90" />
            <el-table-column prop="variant" label="实验组" min-width="90" />
            <el-table-column prop="count" label="次数" width="90" />
            <el-table-column prop="user_count" label="用户数" width="90" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card style="margin-top: 12px;">
      <template #header>日趋势</template>
      <el-table :data="trend.list || []" stripe border size="small" v-loading="loading">
        <el-table-column prop="date" label="日期" min-width="120" />
        <el-table-column prop="page_view" label="访问" width="100" />
        <el-table-column prop="click_buy" label="点击购买" width="120" />
        <el-table-column prop="buy_success" label="支付成功" width="120" />
        <el-table-column label="点击率" width="120">
          <template #default="{ row }">{{ ratio(row.click_buy, row.page_view) }}</template>
        </el-table-column>
        <el-table-column label="支付率" width="120">
          <template #default="{ row }">{{ ratio(row.buy_success, row.click_buy) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { adminApi } from '../api/admin'

const days = ref(7)
const loading = ref(false)
const metrics = ref({ list: [] })
const funnel = ref({ steps: [] })
const trend = ref({ list: [] })

const toPercent = (v) => `${((Number(v) || 0) * 100).toFixed(1)}%`
const ratio = (a, b) => {
  const numerator = Number(a) || 0
  const denominator = Number(b) || 0
  if (denominator <= 0) return '0.0%'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

const summary = computed(() => {
  const map = {}
  ;(funnel.value.steps || []).forEach((step) => {
    map[step.event_name] = step
  })
  const pageView = Number(map.subscription_page_view?.event_count || 0)
  const clickBuy = Number(map.subscription_click_buy?.event_count || 0)
  const buySuccess = Number(map.subscription_buy_success?.event_count || 0)
  return {
    pageView,
    clickBuy,
    buySuccess,
    clickRate: ratio(clickBuy, pageView),
    payRate: ratio(buySuccess, clickBuy)
  }
})

async function loadAll() {
  loading.value = true
  try {
    const [metricsData, funnelData, trendData] = await Promise.all([
      adminApi.fortuneSubscriptionMetrics({ days: days.value }),
      adminApi.fortuneSubscriptionFunnel({ days: days.value }),
      adminApi.fortuneSubscriptionTrend({ days: days.value })
    ])
    metrics.value = metricsData || { list: [] }
    funnel.value = funnelData || { steps: [] }
    trend.value = trendData || { list: [] }
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

onMounted(loadAll)
</script>

<style scoped>
.stat-label {
  color: #6b7280;
  font-size: 13px;
}

.stat-value {
  margin-top: 8px;
  font-size: 26px;
  font-weight: 700;
}
</style>
